import { AUDITOR_DEFAULT_DAYS, GRANTABLE_ROLES, GRANTORS, type CreateUserResponse, type GrantResponse, type GrantSummary, type GrantableRole, type InvitationEmail, type InviteResponse, type UserSummary, type UsersResponse } from "@lms/shared-types";
import { useCallback, useEffect, useId, useState } from "react";
import { ApiRequestError, fetchJson } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { useStepUp } from "../../auth/StepUpProvider";
import { Button, DataTable, Dialog, DialogAlert, EmptyState, ErrorState, LoadingState, PageHeader, RelativeTime, StatusPill, TextField, type Tone } from "../../components/ui";
import { useAnnounce } from "../../lib/announce";
import { ROLE_LABEL } from "../account/AccountPage";

/**
 * Users (PLT-03, design.md §16 "Users and roles"): the people the caller's
 * scope reaches, with status in words, roles by name, last seen; invite,
 * re-invite, disable, enable, remove (a 30-day clock) and the roles
 * dialog — every one a step-up write, so each asks "confirm it's you"
 * when the sign-in is older than fifteen minutes. A remove says what it
 * does before it does it. The roles offered are the ones the caller may
 * confer (permission-matrix.md §5), nothing else.
 */
/**
 * What happened to an invitation's email, in words (PLT-07, SEC-08). A sign-in exists in every case; the words say
 * whether the person will get the email, and what to do when they will not.
 */
/** True when an invitation's email did not go — the one outcome the page keeps in view after the announcement. */
export const emailMissed = (email: InvitationEmail | undefined): boolean => email !== undefined && email !== "sent" && email !== "sent_by_cognito";

export function invitationWords(who: string, email: InvitationEmail | undefined, again = false): string {
  switch (email) {
    case "not_allowed_here":
      return `${who} ${again ? "was re-invited" : "is invited"}, but no email was sent: this test environment emails only its allow-list.`;
    case "suppressed":
      return `${who} ${again ? "was re-invited" : "is invited"}, but no email was sent: earlier mail to that address bounced or was reported, so nothing more is sent to it.`;
    case "unverifiable":
      return `${who} ${again ? "was re-invited" : "is invited"}, but no email was sent: the list of undeliverable addresses could not be checked just now. Try Re-invite in a minute.`;
    case "failed":
      return `${who} ${again ? "was re-invited" : "is invited"}, but the email could not be sent. Try Re-invite.`;
    case "failed_after_reset":
      return `${who} was re-invited, but the email could not be sent, and the earlier temporary password no longer works. Try Re-invite.`;
    default:
      return again ? `Invitation sent again to ${who}.` : `${who} is invited; the email is on its way.`;
  }
}

const STATUS: Record<UserSummary["status"], { word: string; tone: Tone }> ={ invited: { word: "Invited", tone: "accent" }, active: { word: "Active", tone: "ok" }, disabled: { word: "Disabled", tone: "warn" }, deleted: { word: "Removed", tone: "neutral" } };

/** Every mutation shows its error (qa-battery.md §1.1 rule 4): the API's own words when it has them, the error's otherwise — never a bare "something went wrong". */
function errorText(error: unknown): string {
  if (error instanceof ApiRequestError) return error.requestId ? `${error.message} (request ${error.requestId})` : error.message;
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export function UsersPage() {
  const { session, config, me } = useAuth();
  const { ensureFresh } = useStepUp();
  const announce = useAnnounce();
  const [state, setState] = useState<{ users: UserSummary[]; loading: boolean; error?: unknown }>({ users: [], loading: true });
  const [dialog, setDialog] = useState<{ kind: "invite" } | { kind: "roles"; user: UserSummary } | { kind: "remove"; user: UserSummary } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>();
  const [notice, setNotice] = useState<string>();

  // The request-tick pattern (SecurityPage): the effect fetches, an action bumps the tick to fetch again.
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    if (!session) return;
    let live = true;
    fetchJson<UsersResponse>(config, session, "/v1/users")
      .then((r) => live && setState({ users: r.users, loading: false }))
      .catch((error: unknown) => live && setState({ users: [], loading: false, error }));
    return () => {
      live = false;
    };
  }, [config, session, tick]);

  // Resolves true only when the request went through: a dialog closes on that, never on a refusal —
  // the error belongs inside the dialog with the person's input still there (gairm/lms#203, walk 3).
  const act = async <T,>(id: string, label: string, run: () => Promise<T>, said: string | ((result: T) => string), keep?: (result: T) => boolean): Promise<boolean> => {
    setBusy(id);
    setActionError(undefined);
    setNotice(undefined);
    try {
      await ensureFresh();
      const result = await run();
      reload();
      const words = typeof said === "function" ? said(result) : said;
      announce(words);
      // A success with a caveat (an invitation whose email did not go): said in the live region AND left on the page.
      if (keep?.(result)) setNotice(words);
      return true;
    } catch (error) {
      setActionError(error);
      announce(`${label} failed: ${errorText(error)}`);
      return false;
    } finally {
      setBusy(null);
    }
  };

  const canGrantSomething = (GRANTABLE_ROLES as readonly GrantableRole[]).some((role) => GRANTORS[role].some((g) => me?.roles.includes(g)));

  if (!session || !me) return null;
  return (
    <>
      <PageHeader eyebrow="Program" title="Users" subtitle="Everyone in your reach: invite, roles, disable, remove." actions={<Button variant="primary" onClick={() => { setActionError(undefined); setDialog({ kind: "invite" }); }}>Invite a person</Button>} />
      {actionError !== undefined && dialog === null && (
        <p className="mb-4 text-danger" role="alert">
          {errorText(actionError)}
        </p>
      )}
      {notice !== undefined && (
        <p className="mb-4" data-testid="users-notice">
          <StatusPill tone="warn">No email</StatusPill> {notice}
        </p>
      )}
      {state.loading ? (
        <LoadingState label="Loading people…" />
      ) : state.error ? (
        <ErrorState title="The people could not be loaded" error={state.error} onRetry={reload} />
      ) : (
        <DataTable
          caption="People"
          rows={state.users}
          rowKey={(u) => u.id}
          rowTestId={(u) => `user-${u.id}`}
          empty={<EmptyState title="Nobody yet" hint="Invite the first person; they get an email with a temporary password." />}
          initialSort={{ key: "name", dir: "ascending" }}
          columns={[
            { key: "name", header: "Name", render: (u) => (u.id === me.person.id ? `${u.displayName} (you)` : u.displayName), sortValue: (u) => u.displayName },
            { key: "email", header: "Email", render: (u) => u.email, sortValue: (u) => u.email },
            { key: "status", header: "Status", render: (u) => <StatusPill tone={STATUS[u.status].tone}>{u.status === "disabled" && u.deleteRequestedAt ? "Removal in progress" : STATUS[u.status].word}</StatusPill>, sortValue: (u) => u.status },
            { key: "roles", header: "Roles", render: (u) => (u.grants.length ? u.grants.map((g) => `${ROLE_LABEL[g.role] ?? g.role}${g.scopeName ? ` — ${g.scopeName}` : ""}`).join("; ") : <span className="text-muted">Learner only</span>) },
            { key: "seen", header: "Last seen", render: (u) => (u.lastSeenAt ? <RelativeTime iso={u.lastSeenAt} /> : u.hasSignIn ? "Not yet" : "No sign-in yet"), sortValue: (u) => u.lastSeenAt ?? "" },
            {
              key: "actions",
              header: "Actions",
              render: (u) => (
                <div className="flex flex-wrap gap-2">
                  {u.id !== me.person.id && (
                    <>
                      {canGrantSomething && (
                        <Button onClick={() => { setActionError(undefined); setDialog({ kind: "roles", user: u }); }} disabled={busy === u.id}>
                          Roles
                        </Button>
                      )}
                      {u.status === "invited" && (
                        <Button onClick={() => void act(u.id, "Re-invite", () => fetchJson<InviteResponse>(config, session, `/v1/users/${u.id}/invite`, { method: "POST" }), (r) => invitationWords(u.email, r.email, true), (r) => emailMissed(r.email))} disabled={busy === u.id}>
                          Re-invite
                        </Button>
                      )}
                      {u.status === "disabled" ? (
                        <Button onClick={() => void act(u.id, "Enable", () => fetchJson(config, session, `/v1/users/${u.id}/enable`, { method: "POST" }), `${u.displayName} is active again.`)} disabled={busy === u.id}>
                          {u.deleteRequestedAt ? "Cancel removal" : "Enable"}
                        </Button>
                      ) : (
                        <Button onClick={() => void act(u.id, "Disable", () => fetchJson(config, session, `/v1/users/${u.id}/disable`, { method: "POST" }), `${u.displayName} is disabled and signed out everywhere.`)} disabled={busy === u.id}>
                          Disable
                        </Button>
                      )}
                      {!u.deleteRequestedAt && (
                        <Button variant="danger" onClick={() => { setActionError(undefined); setDialog({ kind: "remove", user: u }); }} disabled={busy === u.id}>
                          Remove
                        </Button>
                      )}
                    </>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
      {dialog?.kind === "invite" && (
        <InviteDialog
          error={actionError}
          onClose={() => setDialog(null)}
          onInvite={(input) =>
            act("invite", "Invite", () => fetchJson<CreateUserResponse>(config, session, "/v1/users", { method: "POST", body: JSON.stringify(input), headers: { "content-type": "application/json" } }), (r) => invitationWords(`${input.givenName} ${input.familyName}`, r.email), (r) => emailMissed(r.email)).then((ok) => ok && setDialog(null))
          }
        />
      )}
      {dialog?.kind === "remove" && (
        <Dialog title={`Remove ${dialog.user.displayName}?`} onClose={() => setDialog(null)} closeLabel="Keep">
          {actionError !== undefined && (
            <DialogAlert focusKey={actionError} className="mb-3 text-danger">
              {errorText(actionError)}
            </DialogAlert>
          )}
          <p>Their sign-in ends now and they are disabled at once. Thirty days from now the account is removed for good; until then, Enable brings them back. Their records — hours, agreements, the audit trail — stay, because those belong to the program.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="danger" onClick={() => void act(dialog.user.id, "Remove", () => fetchJson(config, session, `/v1/users/${dialog.user.id}`, { method: "DELETE" }), `${dialog.user.displayName} is disabled; removal in 30 days.`).then((ok) => ok && setDialog(null))}>
              Remove in 30 days
            </Button>
          </div>
        </Dialog>
      )}
      {dialog?.kind === "roles" && (
        <RolesDialog
          user={state.users.find((u) => u.id === dialog.user.id) ?? dialog.user}
          error={actionError}
          onClose={() => setDialog(null)}
          onGrant={(role, scopeId) => act(dialog.user.id, "Grant", () => fetchJson<GrantResponse>(config, session, `/v1/users/${dialog.user.id}/roles`, { method: "POST", body: JSON.stringify({ role, scopeId, reason: `granted from Users by ${me.person.email}` }), headers: { "content-type": "application/json" } }), `${ROLE_LABEL[role]} granted to ${dialog.user.displayName}.`)}
          onRevoke={(grant) => act(dialog.user.id, "Revoke", () => fetchJson(config, session, `/v1/users/${dialog.user.id}/roles/${grant.id}`, { method: "DELETE" }), `${ROLE_LABEL[grant.role] ?? grant.role} revoked from ${dialog.user.displayName}.`)}
        />
      )}
    </>
  );
}

function InviteDialog({ error, onClose, onInvite }: { error: unknown; onClose: () => void; onInvite: (input: { email: string; givenName: string; familyName: string }) => Promise<unknown> }) {
  const [form, setForm] = useState({ email: "", givenName: "", familyName: "" });
  const [busy, setBusy] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && form.givenName.trim() && form.familyName.trim();
  return (
    <Dialog title="Invite a person" onClose={onClose}>
      {error !== undefined && (
        <DialogAlert focusKey={error} className="mb-3 text-danger">
          {errorText(error)}
        </DialogAlert>
      )}
      <form
        className="grid gap-3"
        onSubmit={(ev) => {
          ev.preventDefault();
          if (!valid) return;
          setBusy(true);
          void onInvite({ email: form.email.trim().toLowerCase(), givenName: form.givenName.trim(), familyName: form.familyName.trim() }).finally(() => setBusy(false));
        }}
      >
        <TextField label="Given name" value={form.givenName} onChange={(ev) => setForm({ ...form, givenName: ev.target.value })} required maxLength={80} autoComplete="off" />
        <TextField label="Family name" value={form.familyName} onChange={(ev) => setForm({ ...form, familyName: ev.target.value })} required maxLength={80} autoComplete="off" />
        <TextField label="Email" type="email" description="They receive a temporary password at this address; it expires in three days." value={form.email} onChange={(ev) => setForm({ ...form, email: ev.target.value })} required autoComplete="off" />
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={!valid || busy}>
            {busy ? "Inviting…" : "Send the invitation"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function RolesDialog({ user, error, onClose, onGrant, onRevoke }: { user: UserSummary; error: unknown; onClose: () => void; onGrant: (role: GrantableRole, scopeId: string) => Promise<unknown>; onRevoke: (grant: GrantSummary) => Promise<unknown> }) {
  const { me } = useAuth();
  const grantable = (GRANTABLE_ROLES as readonly GrantableRole[]).filter((role) => GRANTORS[role].some((g) => me?.roles.includes(g)));
  const programs = (me?.grants ?? []).filter((g) => g.scopeKind === "program");
  const [role, setRole] = useState<GrantableRole | "">(grantable[0] ?? "");
  const [scopeId, setScopeId] = useState<string>(programs[0]?.scopeId ?? "");
  const needsProgram = role !== "" && role !== "tenant-admin" && role !== "employer";
  const roleId = useId();
  const programId = useId();
  return (
    <Dialog title={`Roles for ${user.displayName}`} onClose={onClose} closeLabel="Done">
      {error !== undefined && (
        <DialogAlert focusKey={error} className="mb-3 text-danger">
          {errorText(error)}
        </DialogAlert>
      )}
      <h3 className="font-semibold">Held now</h3>
      {user.grants.length === 0 ? (
        <p className="text-muted">Learner only.</p>
      ) : (
        <ul className="mt-2 grid gap-2">
          {user.grants.map((g) => (
            <li key={g.id} className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {ROLE_LABEL[g.role] ?? g.role}
                {g.scopeName ? ` — ${g.scopeName}` : ""}
                {g.expiresAt ? ` (until ${g.expiresAt.slice(0, 10)})` : ""}
              </span>
              <Button variant="danger" onClick={() => void onRevoke(g)}>
                Revoke {ROLE_LABEL[g.role] ?? g.role}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {grantable.length > 0 && (
        <form
          className="mt-4 grid gap-3 border-t border-line pt-4"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (role) void onGrant(role, role === "tenant-admin" ? "" : role === "employer" ? scopeId : scopeId);
          }}
        >
          <h3 className="font-semibold">Grant a role</h3>
          <div className="grid gap-1">
            <label htmlFor={roleId}>Role</label>
            <select id={roleId} className="min-h-11 w-full min-w-0 max-w-full rounded-lg border border-line bg-surface px-3 text-base md:text-sm" value={role} onChange={(ev) => setRole(ev.target.value as GrantableRole)}>
              {grantable.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                  {r === "auditor" ? ` (${AUDITOR_DEFAULT_DAYS} days)` : ""}
                </option>
              ))}
            </select>
          </div>
          {needsProgram && (
            <div className="grid gap-1">
              <label htmlFor={programId}>Program</label>
              <select id={programId} className="min-h-11 w-full min-w-0 max-w-full rounded-lg border border-line bg-surface px-3 text-base md:text-sm" value={scopeId} onChange={(ev) => setScopeId(ev.target.value)}>
                {programs.map((p) => (
                  <option key={p.scopeId} value={p.scopeId}>
                    {p.scopeName ?? p.scopeId}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <Button type="submit" variant="primary" disabled={!role || (needsProgram && !scopeId)}>
              Grant
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
